<script>
import { getSmsClient } from '@/services/sms'

const smsAxios = getSmsClient()

export default {
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    sensorCode: {
      type: [String, Number],
      default: null,
    },
    sensorName: {
      type: String,
      default: '-',
    },
    device: {
      type: [String, Object],
      default: null,
    },
    command: {
      type: Array,
      default: () => [],
    },
    status: {
      type: Boolean,
      default: true,
    },
    region: {
      type: String,
      default: 'SG',
    },
  },
  data() {
    return {
      loadingCommandGroups: false,
      sendingCommands: false,
      sendingCommandId: null,
      commandResults: [],
      checkedAt: null,
    }
  },
  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(value) {
        this.$emit('update:modal', value)
      },
    },
    deviceLabel() {
      if (!this.device) return '-'
      if (typeof this.device === 'string') return this.device

      const deviceId = this.device.imei || this.device.device_id || this.device.deviceId || this.device.id
      const deviceName = this.device.device_name || this.device.deviceName || this.device.name

      return [deviceId, deviceName].filter(Boolean).join(' - ') || '-'
    },
    commandQueryString() {
      if (!this.command.length) return ''
      const params = this.command.map((command) => `command=${encodeURIComponent(command)}`)
      if (this.sensorCode !== null && this.sensorCode !== undefined && this.sensorCode !== '') {
        params.push(`sensor_code=${encodeURIComponent(this.sensorCode)}`)
      }
      return `?${params.join('&')}`
    },
    statusLabel() {
      return this.status ? 'Enable' : 'Disable'
    },

    matchCount() {
      let count = 0
      this.commandResults?.forEach((command) => {
        command.messages?.forEach((message) => {
          if (message.Found) {
            count += 1
          }
        })
      })

      return count
    },
    totalCommand() {
      let count = 0
      this.commandResults?.forEach((command) => {
        command.data?.forEach(() => {
          count += 1
        })
      })
      return count
    },
    checkedTimeText() {
      if (!this.checkedAt) return '-'
      return this.checkedAt.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    
    summaryText() {
      if (this.sendingCommands) {
        return 'Mengirim command ke perangkat...'
      }

      if (this.loadingCommandGroups) {
        return 'Memuat data command pengaturan sensor...'
      }

      if (!this.device?.vehicle_id) {
        return 'Vehicle ID tidak tersedia.'
      }

      if (!this.command.length) {
        return 'Command pengaturan sensor belum tersedia.'
      }

      if (!this.totalCommand) {
        return 'Data command pengaturan sensor belum tersedia.'
      }

      if (this.matchCount === this.totalCommand) {
        return `Semua ${this.commandResults.length} algoritma sesuai dengan expected response. Dicek pukul ${this.checkedTimeText} WIB.`
      }

      return `${this.totalCommand - this.matchCount} dari ${this.totalCommand} algoritma belum sesuai dengan expected response.`
    },
  },
  watch: {
    showModal(value) {
      if (value) {
        this.fetchCommandGroups()
      }
    },
  },
  mounted() {
    if (this.showModal) {
      this.fetchCommandGroups()
    }
  },
  methods: {
    async fetchCommandGroups() {
      if (!this.device?.vehicle_id || !this.command.length) {
        this.commandResults = []
        this.checkedAt = null
        return
      }

      this.loadingCommandGroups = true
      try {
        const response = await smsAxios.get(
          `/command/group/${this.device.vehicle_id}${this.commandQueryString}`
        )
        const responseData = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || []

        this.commandResults = Array.isArray(responseData) ? responseData : []
        this.checkedAt = new Date()
      } catch (error) {
        this.commandResults = []
        this.checkedAt = null
        this.$toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Gagal mengambil data command'
        )
      } finally {
        this.loadingCommandGroups = false
      }
    },
    async handleCheckAgain() {
      if (!this.device.imei || !this.command.length) {
        this.$toast.error('IMEI perangkat atau command tidak tersedia')
        return
      }

      this.sendingCommands = true
      try {
        await Promise.all(this.command.map((command) => this.submitCommandToSms(command)))
        this.$toast.success('Success Send')
        await this.fetchCommandGroups()
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Gagal mengirim command'
        )
      } finally {
        this.sendingCommands = false
      }
    },
    async handleResendCommand(item) {
      if (!item?.command || !this.device.imei) {
        this.$toast.error('IMEI perangkat atau command tidak tersedia')
        return
      }

      this.sendingCommandId = item.id
      try {
        await this.submitCommandToSms(item.command, item.sensorCode)
        this.$toast.success('Success Send')
        await this.fetchCommandGroups()
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Gagal mengirim command'
        )
      } finally {
        this.sendingCommandId = null
      }
    },
    submitCommandToSms(command, sensorCode = this.sensorCode) {
      const payload = {
        command,
        sensor_code: sensorCode,
      }
      const options = {
        method: 'POST',
        url: `/device/${this.device.imei}/command`,
        data: payload,
        headers: {
          'X-Region': this.region,
        },
      }

      return smsAxios(options)
    },
    closeModal() {
      this.showModal = false
    },
  },
}
</script>

<template>
  <b-modal
    v-model="showModal"
    class="mc-modal check-sensor-modal"
    modal-class="check-sensor-modal__dialog"
    size="lg"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
  >
    <div class="check-sensor-modal__header">
      <h4>Pengaturan Bawaan {{ sensorName }}</h4>
    </div>

    <div class="check-sensor-modal__body">
      <div class="check-sensor-modal__device-row">
        <span>Perangkat</span>
        <strong>{{ deviceLabel }}</strong>
      </div>

      <div class="check-sensor-modal__status-card">
        <div>
          <span>Status dari Pengaturan Sensor</span>
          <strong>{{ sensorName || '-' }} · {{ statusLabel }}</strong>
        </div>
        <span
          class="check-sensor-modal__status-badge"
          :class="{ 'check-sensor-modal__status-badge--disable': !status }"
        >
          {{ statusLabel }}
        </span>
      </div>

      <section class="check-sensor-modal__check-card">
        <div class="check-sensor-modal__check-header">
          <div>
            <h5>Cek {{sensorName}}</h5>
            <p>
              Kirim command get untuk membandingkan pengaturan perangkat dengan expected response.
            </p>
            <p>
              Command dikirim dalam interval 5 menit
            </p>
          </div>
          <b-button
            variant="outline-primary"
            class="check-sensor-modal__retry-button"
            :disabled="loadingCommandGroups || sendingCommands || !device?.vehicle_id"
            @click="handleCheckAgain"
          >
            <b-spinner v-if="loadingCommandGroups || sendingCommands" small type="grow"></b-spinner>
            {{ loadingCommandGroups || sendingCommands ? 'Loading...' : 'Cek ulang' }}
          </b-button>
        </div>

        <hr />

        <p
          class="check-sensor-modal__summary"
          :class="{ 'check-sensor-modal__summary--warning': matchCount !== totalCommand }"
        >
          {{ summaryText }}
        </p>

        <div class="check-sensor-modal__result-list">
          <div v-if="loadingCommandGroups" class="check-sensor-modal__empty">
            Loading data command...
          </div>

          <div v-else-if="!commandResults.length" class="check-sensor-modal__empty">
            Belum ada data command untuk ditampilkan.
          </div>

          <div
            v-else
            v-for="item in commandResults"
            :key="item.id"
            class="check-sensor-modal__result-item"
            :class="{ 'check-sensor-modal__result-item--invalid': !item.is_valid }"
          >
            <div>
              <strong>{{ item.title }}</strong>
              <span>{{ item.command }}</span>
              <template>
                <div
                  v-for="(message, messageIndex) in item.messages"
                  :key="`${item.id}-${messageIndex}`"
                >
                  <div class="check-sensor-modal__summary" :class="{ 'check-sensor-modal__summary--warning': message.Found == false}">
                    Expected: {{ message.Expected || '-' }}<br />
                    Response: {{ message.Message || '-' }}
                  </div>
                </div>
              </template>
            </div>

            <div class="check-sensor-modal__result-action">
              <span
                class="check-sensor-modal__result-badge"
                :class="{ 'check-sensor-modal__result-badge--invalid': !item.is_valid }"
              >
                {{ item.is_valid ? 'Sesuai' : 'Tidak sesuai' }}
              </span>
              <b-button
                v-if="!item.is_valid"
                variant="outline-primary"
                size="sm"
                :disabled="sendingCommandId === item.id || loadingCommandGroups || sendingCommands"
                @click="handleResendCommand(item)"
              >
                <b-spinner v-if="sendingCommandId === item.id" small type="grow"></b-spinner>
                {{ sendingCommandId === item.id ? 'Loading...' : 'Kirim command' }}
              </b-button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="check-sensor-modal__footer">
      <b-button variant="outline-primary" @click="closeModal">Tutup</b-button>
    </div>
  </b-modal>
</template>

<style lang="scss">
.check-sensor-modal__dialog {
  .modal-content {
    overflow: hidden;
    border: 0;
    border-radius: 6px;
  }

  .modal-body {
    padding: 0;
  }
}

.check-sensor-modal {
  &__header {
    padding: 16px 22px;
    background: #174f9f;

    h4 {
      margin: 0;
      color: #fff;
      font-size: 20px;
      font-weight: 700;
      line-height: 1.4;
    }
  }

  &__body {
    padding: 26px 22px 24px;
  }

  &__device-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 22px;
    color: #7a869a;
    font-size: 14px;

    strong {
      color: #07152f;
      font-weight: 700;
      text-align: right;
      overflow-wrap: anywhere;
    }
  }

  &__status-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
    padding: 16px;
    border-radius: 6px;
    background: #f7f9fc;

    span {
      display: block;
      color: #7a869a;
      font-size: 14px;
      line-height: 1.4;
    }

    strong {
      display: block;
      color: #07152f;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.4;
    }
  }

  &__status-badge,
  &__result-badge {
    flex-shrink: 0;
    padding: 6px 10px;
    border-radius: 5px;
    color: #02b875 !important;
    background: #e7fbf2;
    font-size: 14px !important;
    font-weight: 600;
    max-height: 32px;
  }

  &__status-badge--disable,
  &__result-badge--invalid {
    color: #e55353 !important;
    background: #fdecec;
    max-height: 32px;
  }

  &__check-card {
    padding: 18px 16px;
    border: 1px solid #d8dee8;
    border-radius: 8px;
  }

  &__check-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    h5 {
      margin: 0 0 6px;
      color: #07152f;
      font-size: 16px;
      font-weight: 700;
    }

    p {
      max-width: 340px;
      margin: 0;
      color: #7582a1;
      font-size: 14px;
      line-height: 1.35;
    }
  }

  &__retry-button {
    min-width: 88px;
    min-height: 36px;
    padding-right: 12px;
    padding-left: 12px;
    font-weight: 600;
    white-space: normal;
  }

  hr {
    margin: 16px 0 12px;
    border-color: #e4e9f1;
  }

  &__summary {
    margin: 0 0 12px;
    color: #008b5a;
    font-size: 14px;
    line-height: 1.4;

    &--warning {
      color: #e55353;
    }
  }

  &__result-list {
    max-height: 342px;
    overflow-y: auto;
  }

  &__result-action {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;

    .btn {
      white-space: nowrap;
    }
  }

  &__result-item {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 8px;
    border-bottom: 1px solid #e4e9f1;

    > div {
      min-width: 0;
    }

    &:last-child {
      border-bottom: 0;
    }

    strong,
    span,
    small {
      display: block;
    }

    strong {
      margin-bottom: 2px;
      color: #07152f;
      font-size: 15px;
      font-weight: 700;
    }

    span {
      margin-bottom: 6px;
      color: #52627f;
      font-size: 12px;
      overflow-wrap: anywhere;
    }

    small {
      color: #07152f;
      font-size: 14px;
      line-height: 1.45;
      overflow-wrap: anywhere;
    }
  }

  &__empty {
    padding: 18px 12px;
    color: #7582a1;
    font-size: 14px;
    text-align: center;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px 22px;
    border-top: 1px solid #e4e9f1;
  }
}

@media (max-width: 575px) {
  .check-sensor-modal {
    &__body {
      padding: 20px 16px;
    }

    &__device-row,
    &__check-header,
    &__result-item {
      flex-direction: column;
    }

    &__device-row strong {
      text-align: left;
    }

    &__result-badge {
      align-self: flex-start;
    }

    &__result-action {
      align-items: flex-start;
    }
  }
}
</style>
